import classNames from 'classnames'
import React from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import { CiSearch } from 'react-icons/ci'
import { useNavigate } from 'react-router-dom'

import { useCursorTooltip } from '../../../hooks/useCursorTooltip.ts'
import useOrdersHistory from '../../../hooks/useOrdersHistory.ts'
import { convertDate } from '../../../utils/dates.ts'
import Alert from '../../common/Alert/Alert.tsx'
import Badge from '../../common/Badge/Badge.tsx'
import Card from '../../common/Card/Card.tsx'
import { CursorTooltip } from '../../common/CursorTooltip'
import HoverHint from '../../common/HoverHint'

const ProfileOrdersHistory = ({
  title = 'Historia zamówień pacjenta',
  patientProfileId,
  currentOrderId,
  actions,
  colSpans = [1, 2, 1, 3]
}: {
  title?: string
  patientProfileId: number
  currentOrderId: number
  actions?: {
    label: string
    colSpan: number
  }
  colSpans?: number[]
}) => {
  const { tooltip, showTooltip, moveTooltip, hideTooltip } = useCursorTooltip({
    cursorOffset: 12
  })

  const { loading, ordersList } = useOrdersHistory({
    currentOrderId,
    patientProfileId
  })

  const columnsHeader = [
    {
      title: 'Nr',
      span: colSpans[0]
    },
    {
      title: 'Data',
      span: colSpans[1]
    },
    {
      title: 'Okres',
      span: colSpans[2]
    },
    {
      title: 'Status',
      span: colSpans[3]
    }
  ]

  const baseCols = colSpans.reduce((sum, s) => sum + s, 0)
  const totalCols = baseCols + (actions?.colSpan ?? 0)

  const gridStyle = React.useMemo<React.CSSProperties>(
    () => ({
      gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`
    }),
    [totalCols]
  )

  const navigate = useNavigate()

  const onRowClick = (orderId: number | string) => {
    navigate(`/orders/${orderId}`)
  }

  return (
    <React.Fragment>
      <Card.Title>
        <div className="h-10 flex flex-row justify-between items-center">
          <span className="mr-3">{title}</span>
        </div>
      </Card.Title>

      <Card.Body>
        {loading && <p className="text-sm text-gray-500">Loading...</p>}

        <div className="mt-4 overflow-hidden border border-gray-300">
          {/* Header */}
          <div
            style={gridStyle}
            className={classNames(
              'grid gap-3 bg-white text-black dark:bg-meta-4 dark:text-white px-4 py-4 font-medium tracking-wide'
            )}
          >
            {columnsHeader.map((column, index) => (
              <div
                key={index}
                style={{
                  gridColumn: `span ${colSpans[index]} / span ${colSpans[index]}`
                }}
                className="text-left"
              >
                {column.title}
              </div>
            ))}

            {actions && (
              <div
                style={{
                  gridColumn: `span ${actions.colSpan} / span ${actions.colSpan}`
                }}
                className="text-left"
              >
                Wiecej
              </div>
            )}
          </div>

          {/* Rows */}
          <div className="divide-y dark:divide-gray-700">
            {ordersList?.length
              ? ordersList.map((o) => (
                  <div
                    key={o.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => (actions ? null : onRowClick(o.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onRowClick(o.id)
                    }}
                    onMouseEnter={(e) =>
                      actions
                        ? null
                        : showTooltip(
                            e,
                            `Kliknij, aby przejść do zamówienia nr ${o.id}`
                          )
                    }
                    onMouseMove={actions ? () => null : moveTooltip}
                    onMouseLeave={hideTooltip}
                    style={gridStyle}
                    className={classNames(
                      actions ? '' : 'cursor-pointer',
                      actions ? 'text-sm md:text-[16px]' : 'text-sm',
                      actions ? '' : 'hover:bg-gray-200',
                      'odd:bg-gray-50 even:bg-white dark:odd:bg-gray-800 dark:even:bg-gray-900 relative grid gap-3 px-4 py-4 text-gray-500 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
                    )}
                  >
                    <div
                      style={{
                        gridColumn: `span ${colSpans[0]} / span ${colSpans[0]}`
                      }}
                      className="truncate font-medium"
                    >
                      {o.id}
                    </div>

                    <div
                      style={{
                        gridColumn: `span ${colSpans[1]} / span ${colSpans[1]}`
                      }}
                      className="truncate font-medium"
                    >
                      {convertDate(new Date(o.created_at))}
                    </div>

                    <div
                      style={{
                        gridColumn: `span ${colSpans[2]} / span ${colSpans[2]}`
                      }}
                      className="truncate font-medium"
                    >
                      {o.subscription_length * 30} dni
                    </div>

                    <div
                      style={{
                        gridColumn: `span ${colSpans[3]} / span ${colSpans[3]}`
                      }}
                      className="font-medium"
                    >
                      <Badge status={o.status} />
                    </div>

                    {actions && (
                      <div
                        style={{
                          gridColumn: `span ${actions.colSpan} / span ${actions.colSpan}`
                        }}
                        className="flex justify-center"
                      >
                        <HoverHint
                          hintLabel={actions.label}
                          position={'top'}
                          align={'center'}
                        >
                          <div
                            onClick={() => onRowClick(o.id)}
                            className="flex flex-row items-center text-blue-500 cursor-pointer"
                          >
                            <CiSearch className="text-2xl" />
                          </div>
                        </HoverHint>
                      </div>
                    )}
                  </div>
                ))
              : !loading && (
                  <div>
                    <Alert
                      type="info"
                      message="Brak danych"
                      icon={<AiOutlineInfoCircle className="text-lg" />}
                    />
                  </div>
                )}
          </div>
        </div>

        <CursorTooltip tooltip={tooltip} />
      </Card.Body>
    </React.Fragment>
  )
}

export default ProfileOrdersHistory
